import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { PermissionsService } from '../../../shared/services/permissions.service';
import { FormMode } from '../../../shared/types/forms.types';
import { ResourceDTO, ScopeDTO } from '../../../shared/types/permission.types';

@Component({
  selector: 'app-resource-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './resource-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResourceFormComponent implements OnInit, OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly permissionsService = inject(PermissionsService);

  @Input() resource: ResourceDTO | null = null;
  @Input() mode: FormMode = 'create';
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly save = new EventEmitter<ResourceDTO>();
  @Output() readonly cancel = new EventEmitter<void>();

  readonly availableScopes = signal<ScopeDTO[]>([]);
  readonly selectedScopeNames = signal<Set<string>>(new Set());
  readonly loadingScopes = signal(false);

  readonly form = this.fb.group({
    name: [
      '',
      [Validators.required, Validators.maxLength(80), Validators.pattern(/^[a-zA-Z0-9_\-.: ]+$/)],
    ],
    displayName: [''],
    type: [''],
    urisRaw: [''], // one URI per line
    iconUri: [''],
    ownerManagedAccess: [false],
  });

  ngOnInit(): void {
    this.loadScopes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resource'] && this.resource) {
      this.form.patchValue(
        {
          name: this.resource.name ?? '',
          displayName: this.resource.displayName ?? '',
          type: this.resource.type ?? '',
          urisRaw: (this.resource.uris ?? []).join('\n'),
          iconUri: this.resource.icon_uri ?? '',
          ownerManagedAccess: this.resource.ownerManagedAccess ?? false,
        },
        { emitEvent: false },
      );
      this.selectedScopeNames.set(
        new Set((this.resource.scopes ?? []).map((s) => s.name)),
      );
    }
    if (changes['mode'] && this.mode === 'edit') {
      this.form.controls.name.disable({ emitEvent: false });
    }
  }

  loadScopes(): void {
    this.loadingScopes.set(true);
    this.permissionsService.listScopes().subscribe({
      next: (res) => {
        this.availableScopes.set((res ?? []).sort((a, b) => a.name.localeCompare(b.name)));
        this.loadingScopes.set(false);
      },
      error: () => this.loadingScopes.set(false),
    });
  }

  toggleScope(name: string): void {
    const next = new Set(this.selectedScopeNames());
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.selectedScopeNames.set(next);
  }

  isScopeSelected(name: string): boolean {
    return this.selectedScopeNames().has(name);
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const uris = raw.urisRaw
      .split(/\r?\n/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0);

    const scopes: ScopeDTO[] = [...this.selectedScopeNames()]
      .map((name) => this.availableScopes().find((s) => s.name === name))
      .filter((s): s is ScopeDTO => !!s);

    const payload: ResourceDTO = {
      ...(this.resource ?? {}),
      name: raw.name.trim(),
      displayName: raw.displayName?.trim() || undefined,
      type: raw.type?.trim() || undefined,
      uris: uris.length ? uris : undefined,
      icon_uri: raw.iconUri?.trim() || undefined,
      ownerManagedAccess: raw.ownerManagedAccess,
      scopes,
    };
    this.save.emit(payload);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }

  errorFor(name: keyof typeof this.form.controls): string | null {
    const c: AbstractControl = this.form.controls[name];
    if (!c.errors || (!c.touched && !c.dirty)) return null;
    if (c.errors['required']) return 'Champ obligatoire.';
    if (c.errors['maxlength']) return `${c.errors['maxlength'].requiredLength} caractères max.`;
    if (c.errors['pattern']) return 'Caractères invalides.';
    return 'Valeur invalide.';
  }
}
